import { Button, FAIcon } from '@/components'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { format } from '@/utils/date-fns'
import { isAfter, setMonth, setYear } from 'date-fns'
import capitalize from 'lodash/capitalize'
import range from 'lodash/range'
import { useState } from 'react'

interface MonthYearPickerProps {
  value: Date
  onChange: (value: Date) => void
  /** Disable months/years after the current month. Defaults to true. */
  disableAfter?: boolean
  /** Earliest selectable year. Defaults to current year - 4. */
  earliestYear?: number
  /** How many years past the current year the navigation allows. Defaults 0. */
  futureYears?: number
  className?: string
  id?: string
}

export function MonthYearPicker({
  value,
  onChange,
  disableAfter = true,
  earliestYear = new Date().getFullYear() - 4,
  futureYears = 0,
  className,
  id,
}: MonthYearPickerProps) {
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(value.getFullYear())

  const now = new Date()
  const maxYear = now.getFullYear() + Math.max(0, futureYears)

  const isMonthDisabled = (month: number) =>
    disableAfter && isAfter(new Date(viewYear, month, 1), now)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) setViewYear(value.getFullYear())
  }

  const handleSelect = (month: number) => {
    if (isMonthDisabled(month)) return
    onChange(setYear(setMonth(value, month), viewYear))
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            'w-full min-w-[200px] justify-between border-gray-200 bg-quaternary px-4 font-normal',
            className
          )}
        >
          <span className="flex items-center gap-2">
            <FAIcon icon="calendar" className="text-gray-400" />
            {capitalize(format(value, 'LLLL'))} {value.getFullYear()}
          </span>
          <FAIcon icon="chevron-down" className="text-gray-300" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-72">
        <div className="mb-3 flex items-center justify-between">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            disabled={viewYear <= earliestYear}
            aria-label="Previous year"
            onClick={() => setViewYear(year => year - 1)}
          >
            <FAIcon icon="chevron-left" />
          </Button>
          <span className="text-sm font-semibold text-gray-800">{viewYear}</span>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            disabled={viewYear >= maxYear}
            aria-label="Next year"
            onClick={() => setViewYear(year => year + 1)}
          >
            <FAIcon icon="chevron-right" />
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {range(12).map(month => {
            const selected =
              month === value.getMonth() && viewYear === value.getFullYear()
            const disabled = isMonthDisabled(month)
            return (
              <button
                key={month}
                type="button"
                disabled={disabled}
                onClick={() => handleSelect(month)}
                className={cn(
                  'rounded-md px-2 py-2 text-sm font-medium transition-colors',
                  selected
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-700 hover:bg-gray-100',
                  disabled && 'cursor-not-allowed text-gray-300 hover:bg-transparent'
                )}
              >
                {capitalize(format(new Date(viewYear, month, 1), 'LLL'))}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
